import React, { useCallback, useEffect, useState } from 'react';
import { Download, Award, Calendar, ExternalLink, Loader } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import CertificateTemplate from '@/components/certificates/CertificateTemplate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_LOGO = `${process.env.PUBLIC_URL}/prasha-logo.jpeg`;

const CERTIFICATE_PRINT_STYLES = `
  @page {
    margin: 0;
    size: landscape;
  }
  body {
    margin: 0;
    padding: 24px;
    background: #f4f6f8;
    font-family: Arial, sans-serif;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .certificate-shell {
    background: #f4f6f8;
    border-radius: 24px;
    padding: 20px;
  }
  .certificate-template {
    position: relative;
    width: 100%;
    min-height: 700px;
    background: #fdfdfb;
    border: 2px solid #0f4c4c;
    box-sizing: border-box;
    overflow: hidden;
    color: #111827;
  }
  .certificate-template::before {
    content: "";
    position: absolute;
    inset: 10px;
    border: 1px solid #e6e6e6;
  }
  .certificate-template::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(15, 76, 76, 0.08) 0%, rgba(15, 76, 76, 0) 70%);
    transform: translate(-50%, -50%);
  }
  .certificate-ribbon {
    position: absolute;
    top: 0;
    right: 0;
    width: 140px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
  }
  .certificate-ribbon-art {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .certificate-ribbon-text {
    position: relative;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    letter-spacing: 3px;
    font-size: 12px;
    color: #fff;
    z-index: 1;
  }
  .certificate-logo {
    position: absolute;
    top: 56px;
    left: 56px;
    width: 110px;
    height: 110px;
    object-fit: contain;
    border-radius: 16px;
    background: #fff;
    padding: 10px;
    border: 1px solid rgba(15, 76, 76, 0.12);
    z-index: 2;
  }
  .certificate-watermark {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(100% - 87.5px);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }
  .certificate-watermark-mark {
    width: 280px;
    height: 280px;
    border-radius: 999px;
    border: 12px solid rgba(15, 76, 76, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 64px;
    font-weight: 700;
    color: rgba(15, 76, 76, 0.08);
    letter-spacing: 4px;
  }
  .certificate-content {
    position: relative;
    z-index: 2;
    padding: 88px 180px 170px 92px;
    text-align: center;
  }
  .certificate-kicker {
    font-size: 18px;
    letter-spacing: 4px;
    color: #0f4c4c;
    margin: 0 0 28px;
    font-weight: 700;
  }
  .certificate-copy {
    font-size: 18px;
    color: #475569;
    margin: 0;
  }
  .certificate-name {
    font-size: 56px;
    letter-spacing: 1px;
    font-family: Georgia, serif;
    margin: 18px 0;
    color: #111827;
    line-height: 1.1;
  }
  .certificate-course {
    font-size: 28px;
    margin: 18px 0 10px;
    color: #1f7a7a;
    font-weight: 700;
  }
  .certificate-details {
    font-size: 15px;
    margin-top: 18px;
    color: #64748b;
  }
  .certificate-meta {
    margin-top: 28px;
    display: flex;
    justify-content: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .certificate-pill {
    border-radius: 999px;
    border: 1px solid rgba(15, 76, 76, 0.15);
    background: rgba(15, 76, 76, 0.04);
    color: #0f4c4c;
    padding: 8px 14px;
    font-size: 13px;
  }
  .certificate-signature {
    position: absolute;
    left: 80px;
    bottom: 72px;
    z-index: 2;
    text-align: center;
    max-width: 260px;
  }
  .certificate-signature-mark {
    font-family: Georgia, serif;
    font-size: 36px;
    color: #0f4c4c;
    margin-bottom: 6px;
  }
  .certificate-signature-label {
    font-size: 14px;
    color: #334155;
    margin: 0;
  }
  .certificate-signature-id {
    font-size: 13px;
    color: #64748b;
    margin: 6px 0 0;
  }
  .certificate-date {
    position: absolute;
    right: 180px;
    bottom: 72px;
    z-index: 2;
    text-align: right;
    color: #334155;
  }
  .certificate-date-label {
    margin: 0 0 6px;
    font-size: 12px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #64748b;
  }
  .certificate-date-value {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
`;

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatLongDate = (value) => (
  value ? new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : ''
);

const looksLikeEmail = (value = '') => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());

const resolveCertificateName = (certificate) => {
  const candidate = (
    certificate?.resolved_user_name?.trim()
    || certificate?.user_name?.trim()
    || ''
  );

  if (!candidate) {
    return 'Learner';
  }

  return looksLikeEmail(candidate) ? candidate.split('@', 1)[0] : candidate;
};

const buildPrintMarkup = (certificate) => {
  const providerName = certificate.provider_name || 'PRASHA';
  const duration = certificate.course_duration || 'Self-paced program';
  const universityIdentifier = certificate.university_identifier || 'Not provided';
  const logoSrc = certificate.university_logo_url || FALLBACK_LOGO;
  const certificateName = resolveCertificateName(certificate);

  return `
    <div class="certificate-shell">
      <div class="certificate-template">
        <img src="${escapeHtml(logoSrc)}" alt="${escapeHtml(providerName)} logo" class="certificate-logo" />
        <div class="certificate-ribbon">
          <svg class="certificate-ribbon-art" viewBox="0 0 140 700" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="certificateRibbonGradientPrint" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#0f4c4c"></stop>
                <stop offset="100%" stop-color="#1f7a7a"></stop>
              </linearGradient>
            </defs>
            <rect width="140" height="700" fill="url(#certificateRibbonGradientPrint)"></rect>
          </svg>
          <div class="certificate-ribbon-text">COURSE CERTIFICATE</div>
        </div>
        <div class="certificate-watermark">
          <div class="certificate-watermark-mark">P</div>
        </div>
        <div class="certificate-content">
          <p class="certificate-kicker">CERTIFICATE OF COMPLETION</p>
          <p class="certificate-copy">This is to certify that</p>
          <h2 class="certificate-name">${escapeHtml(certificateName)}</h2>
          <p class="certificate-copy">has successfully completed</p>
          <h3 class="certificate-course">${escapeHtml(certificate.course_title)}</h3>
          <p class="certificate-details">
            Duration: <span>${escapeHtml(duration)}</span> | Provider: <span>${escapeHtml(providerName)}</span>
          </p>
          <div class="certificate-meta">
            <div class="certificate-pill">Certificate ID: ${escapeHtml(certificate.certificate_id)}</div>
            <div class="certificate-pill">University ID: ${escapeHtml(universityIdentifier)}</div>
          </div>
        </div>
        <div class="certificate-signature">
          <div class="certificate-signature-mark">PRASHA</div>
          <p class="certificate-signature-label">Authorized Signatory</p>
          <p class="certificate-signature-id">Issued by ${escapeHtml(providerName)}</p>
        </div>
        <div class="certificate-date">
          <p class="certificate-date-label">Issued On</p>
          <p class="certificate-date-value">${escapeHtml(formatLongDate(certificate.issue_date))}</p>
        </div>
      </div>
    </div>
  `;
};

const MyCertificates = () => {
  const { token } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCert, setSelectedCert] = useState(null);
  const { toast } = useToast();

  const fetchCertificates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/certificates/my-certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCertificates(data.certificates || []);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load certificates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast, token]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const generatePDF = async (certificate) => {
    try {
      const response = await fetch(
        `${API}/certificates/${certificate.certificate_id}/download`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();
      const certificateData = data.certificate || certificate;
      const printWindow = window.open('', '', 'height=800,width=1200');

      if (!printWindow) {
        throw new Error('Unable to open print window');
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate - ${escapeHtml(certificateData.course_title)}</title>
          <style>${CERTIFICATE_PRINT_STYLES}</style>
        </head>
        <body>
          ${buildPrintMarkup(certificateData)}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => {
        printWindow.print();
      };

      toast({
        title: 'Success',
        description: 'Certificate opened in print view'
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: 'Error',
        description: 'Failed to download certificate',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader className="w-8 h-8 text-[#1a5c5c] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-2">
            <Award className="w-10 h-10 text-[#1a5c5c]" />
            <h1 className="text-4xl font-bold text-slate-900">My Certificates</h1>
          </div>
          <p className="text-slate-600 text-lg">
            Your achievements and course completions
          </p>
        </div>

        {certificates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No certificates yet
            </h3>
            <p className="text-slate-600 mb-6">
              Complete courses to earn certificates and recognize your achievements
            </p>
            <a
              href="/courses"
              className="inline-flex items-center px-6 py-3 bg-[#1a5c5c] text-white rounded-lg hover:bg-[#0f3535] transition"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Browse Courses
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <div
                key={cert.certificate_id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition p-6 border-l-4 border-[#1a5c5c]"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <Award className="w-6 h-6 text-[#1a5c5c] flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900 truncate">
                        {cert.course_title}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(cert.issue_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold whitespace-nowrap ml-2">
                    {cert.status}
                  </span>
                </div>

                <div className="bg-slate-50 rounded p-3 mb-4 text-xs text-slate-600 font-mono break-all">
                  ID: {cert.certificate_id}
                </div>

                <div className="mb-4 space-y-1 text-sm text-slate-600">
                  <p>{cert.provider_name || 'PRASHA'}</p>
                  {cert.university_identifier ? <p>University ID: {cert.university_identifier}</p> : null}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => generatePDF(cert)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#1a5c5c] text-white rounded-lg hover:bg-[#0f3535] transition text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={() => setSelectedCert(cert)}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition text-sm font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedCert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-b from-slate-50 to-white">
                <CertificateTemplate certificate={selectedCert} />
              </div>

              <div className="p-6 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => generatePDF(selectedCert)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#1a5c5c] text-white rounded-lg hover:bg-[#0f3535] transition font-medium"
                >
                  <Download className="w-5 h-5" />
                  Download PDF
                </button>
                <button
                  onClick={() => setSelectedCert(null)}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCertificates;
